import { i18n } from '@lingui/core';
import type { ValidationIssueSeverity } from '@repo/contracts';
import type { PublicValidationIssue } from '@repo/errors';

/**
 * A validation issue as surfaced to the frontend: the canonical public
 * shape (`field` / `message` / `code`) from `@repo/errors` plus the optional
 * severity rotagen attaches (`@repo/contracts` `ValidationIssueSeverity`).
 */
export type ApiValidationIssue = PublicValidationIssue & { severity?: ValidationIssueSeverity };

/**
 * Buckets a flat list of validation issues by their `field` path so a
 * form can render per-input error messages in O(1). Keys are the raw
 * field paths from the backend (e.g. `shifts.0.startTime`); consumers
 * are responsible for matching their inputs to those paths.
 *
 * Generic over the issue shape so callers can pass `RotagenValidationIssue[]`
 * (severity as `string`) without forcing a cast to `ApiValidationIssue[]`
 * (severity as a narrowed union) — the function only reads `field`.
 */
export function bucketIssuesByField<Issue extends { field: string }>(
  issues: Issue[],
): Map<string, Issue[]> {
  const map = new Map<string, Issue[]>();
  for (const issue of issues) {
    const existing = map.get(issue.field);
    if (existing) {
      existing.push(issue);
    } else {
      map.set(issue.field, [issue]);
    }
  }
  return map;
}

/**
 * Translates a backend error `code` via the Lingui catalog (D49). Falls
 * back to `englishFallback` (typically the original `friendlyMessage`
 * from the API envelope) when the code isn't in the catalog.
 *
 * Codes are stored as msgid `errors.<CODE>` in `locale/locales/errors.po`
 * — Phase 5 introduces the helper; the catalog file + concrete entries
 * land alongside the components that consume them (Phase 6 / Phase 7).
 * Until then, every call falls through to `englishFallback`, which is
 * what the existing app does anyway.
 */
export function translateErrorCode(code: string, englishFallback: string): string {
  const key = `errors.${code}`;
  const translated = i18n._(key);
  // Lingui returns the msgid unchanged when no translation exists.
  return translated === key ? englishFallback : translated;
}
